<script
  setup
  lang="ts"
  generic="
    TForm extends {
      setFieldValue: (...args: any[]) => void
      useStore: <TSelected>(
        selector: (state: { values: Record<string, unknown> }) => TSelected
      ) => Ref<TSelected>
    }
  "
>
  import { computed, ref, type Ref } from 'vue'
  import {
    ActionCard,
    ActionCardLeadIcon,
    ActionCardTitle,
    Popover,
    PopoverContent,
    PopoverTrigger
  } from '@gat-ship/vue-components'
  import {
    IconShieldCheck,
    IconShieldAlert,
    IconShieldOff
  } from '@gat-ship/icons'
  import type { FieldRequiredOption } from '@gat-ship/types'

  const props = defineProps<{
    form: TForm
  }>()

  const popoverOpen = ref(false)

  const requiredOption = props.form.useStore(
    (s) => s.values.requiredOption as FieldRequiredOption | undefined
  )

  function handleOptionChange(value: FieldRequiredOption) {
    props.form.setFieldValue('requiredOption', value)
    popoverOpen.value = false
  }

  const validationIcon = computed(() => {
    if (requiredOption.value === 'required') {
      return IconShieldCheck
    }
    if (requiredOption.value === 'optional') {
      return IconShieldOff
    }
    return IconShieldAlert
  })
</script>

<template>
  <Popover v-model:open="popoverOpen" :modal="true">
    <PopoverTrigger as-child>
      <ActionCard>
        <div class="flex w-full flex-col gap-4">
          <div class="flex w-full items-center gap-2">
            <ActionCardLeadIcon :icon="validationIcon" />
            <div class="flex w-full items-center">
              <div>
                <ActionCardTitle> Validation </ActionCardTitle>
              </div>
              <div class="text-muted-foreground ml-auto text-sm font-normal">
                <span v-if="requiredOption === 'required'">
                  Require input
                </span>
                <span v-else> None </span>
              </div>
            </div>
          </div>
        </div>
      </ActionCard>
    </PopoverTrigger>
    <PopoverContent
      class="flex w-[var(--reka-popover-trigger-width)] flex-col gap-5"
    >
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <p class="mb-2 text-sm font-normal">
            Should this field require input?
          </p>
          <div
            class="divide-border border-border flex flex-col divide-y border-t"
          >
            <button
              v-if="requiredOption !== 'optional'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleOptionChange('optional')"
            >
              <IconShieldOff class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">No validation</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field is optional.
                </div>
              </div>
            </button>
            <button
              v-if="requiredOption !== 'required'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleOptionChange('required')"
            >
              <IconShieldCheck class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">Require input</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field requires input to pass validation.
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
